import type { ReactNode } from "react";
import { useT } from "@transifex/react";
import ExerciseCard from "../../../complex/onlineTest/exercises/ExerciseCard";
import { formatScoreWithoutTrailingZero } from "../../../helpers/ScoringHelper";
import { getIsScoreShown } from "../../../helpers/StudentResultScoreReveal";
import type { StudentResultExercise } from "../../../helpers/StudentResultModel";

interface StudentResultExerciseFrameProps {
    exercise: StudentResultExercise;
    children: ReactNode;
}

/**
 * The card every drill-in repeats around its answers. ExerciseWrapper is the same idea but states
 * one total where a result shows reached / max, so this wraps ExerciseCard directly; the card keeps
 * the label inside on mobile for a result.
 *
 * The label needs a scored test *and* the reveal — unscored maxima are a flat 1, so "1 / 1 Points"
 * would only restate the answers' own marks.
 */
const StudentResultExerciseFrame = (props: StudentResultExerciseFrameProps) => {
    const t = useT();

    const t_points = t("Points", { _tags: "StudentResult, ExerciseFrame" });

    const reached = formatScoreWithoutTrailingZero(props.exercise.reachedScore);
    const max = formatScoreWithoutTrailingZero(props.exercise.maxScore);
    const pointsLabel = getIsScoreShown(props.exercise.isScored) ? `${reached} / ${max} ${t_points}` : undefined;

    return (
        <ExerciseCard
            data-testid="student-result-exercise-frame"
            number={props.exercise.number}
            title={props.exercise.description}
            // Nothing left to progress through, and the ring's fill would be a judgment signal.
            isResult
            pointsLabel={pointsLabel}
        >
            {props.children}
        </ExerciseCard>
    );
};

export default StudentResultExerciseFrame;
